/**
 * The **Advanced** disclosure on an Integrations tab: everything that is a
 * decision about an account that already works, folded away from everything
 * that decides whether it works at all.
 *
 * ## The dividing line, and it is not importance
 *
 * The question is *does this affect whether you can send?* — not *is this
 * important?*. Tracking, the redirect subdomain, Virtual Deliverability Manager
 * and the SNS topic are all consequential, and one of them costs money; none of
 * them is why a campaign reached nobody. The SES sandbox is. So they go behind
 * this and the sandbox warning never can, however tidy that would look.
 *
 * The two jobs this page runs together have completely different frequencies:
 * connecting an account happens once and then essentially never again, while
 * the expert configuration is ongoing. Giving both the same visual weight is
 * what made the page scroll, and it is why setting up WhatsApp used to mean
 * scrolling past every SES panel *and* the whole tracking card.
 *
 * ## What is not allowed to change on the way in
 *
 * Moving a control behind a disclosure is a change of prominence. It is not a
 * licence to shorten what the control says, and slice 11's copy is the case
 * that proves it: the tracking switch states, before anybody turns it on, that
 * every message will carry an invisible pixel and every link will be rewritten,
 * and a tenant may have promised somebody that they do not track. That sentence
 * arrives on the wire in `TRACKING_DISCLOSURE` and is rendered verbatim. The
 * VDM cost line — about $0.07 per 1,000 messages, on the tenant's own AWS bill
 * — is the same kind of sentence. Neither may become hover-only, and neither
 * got a word shorter for fitting in here.
 *
 * ## `<details>`, and the position it supersedes
 *
 * This was first written as a `<button aria-expanded>` over a region that
 * mounted its children on first open and kept them mounted afterwards. The
 * argument for it was that nothing behind the disclosure would load until
 * somebody asked for it. That is now the rejected alternative, for two reasons.
 *
 * **A conditionally rendered section cannot be found.** Somebody holding an SNS
 * topic ARN from the AWS console, or a redirect subdomain from a DNS record,
 * will press Ctrl+F to check it against what is stored. Unmounted JSX is not on
 * the page at all, so that search fails in every browser ever written; content
 * inside a closed `<details>` is at least *there*, and browsers that implement
 * find-in-page through a closed disclosure — Chrome has since 102 — open it on
 * the match. Hiding a value behind a disclosure is the point of this component.
 * Hiding it from the browser's own search is how a disclosure becomes the place
 * things go to be lost.
 *
 * **And there was a second disclosure on this screen.** `WhatsAppAdvanced` does
 * the same job on the WhatsApp tab and reached `<details>` independently while
 * the two halves of slice 16 were being written in parallel. Two widgets that
 * look identical and behave differently on one page is the exact accretion this
 * slice exists to undo — so this was written to match it, down to the class
 * recipe. **That hole is now closed:** `WhatsAppAdvanced` renders *this*
 * component, so there is one disclosure implementation on the screen and not
 * two, and a change to the summary affordance moves both tabs together. The
 * paragraph above is kept as the superseded position because it is the reason
 * this component's markup looks the way it does — it was written to match a
 * hand-rolled `<details>` that no longer exists, and somebody tidying the class
 * recipe should know it was once a compatibility constraint rather than a
 * preference.
 *
 * The cost that comes with `<details>` is accepted rather than hidden: the
 * children mount with the page, so `TrackingCard`'s loads run whether or not
 * anybody opens this. That is precisely what the page did before this slice —
 * the tracking card was the second of three stacked cards and always loaded —
 * so it is not a regression, and the browser's find-in-page is worth more than
 * a request that was already being made yesterday.
 *
 * The `<summary>` carries the state, the keyboard behaviour and the disclosure
 * role with no code of ours, which also removes the failure mode of an
 * `aria-expanded` that stops being updated and tells a screen reader the
 * section is closed while it is open.
 */
import { Kicker, cn, focusRing } from '@wend/ui';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

export interface AdvancedDisclosureProps {
  /**
   * What is inside, in a few words, always visible. Not a teaser: somebody
   * scanning for the redirect domain has to be able to tell from the closed
   * state that this is where it went. A disclosure whose label does not name
   * its contents is one people open once, find nothing they wanted, and never
   * open again.
   */
  readonly summary: string;
  /** The word on the control. `'Advanced'` unless a tab has a better one. */
  readonly label?: string;
  /**
   * Whether it starts open. Collapsed is the default and is the whole point of
   * the component; this exists for a caller that already knows the person came
   * for what is inside, and it is how the verify script renders the open state
   * — there is no DOM in this repository to press a summary with.
   */
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
}

export function AdvancedDisclosure({
  summary,
  label = 'Advanced',
  defaultOpen = false,
  children,
}: AdvancedDisclosureProps) {
  return (
    <details className="group rounded-base border border-line bg-surface-2/40" open={defaultOpen}>
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center gap-3 rounded-base px-4 py-3',
          'hover:bg-white/[0.03]',
          '[&::-webkit-details-marker]:hidden',
          focusRing,
        )}
      >
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-muted transition-transform duration-150 ease-wend group-open:rotate-90"
        />
        <Kicker as="span">
          {label}
        </Kicker>
        <span className="min-w-0 text-[0.88rem] text-muted">{summary}</span>
      </summary>

      <div className="flex flex-col gap-6 border-t border-line px-4 py-5">{children}</div>
    </details>
  );
}
